// Final answers a person at the station must see (plan §9.3, D-024), kept in the local-only
// `command_outcome` table by the uploader:
//   held      the server keeps the command for a manager. It stays listed while its hold is
//             open; the `station_holds` stream (review CR3-5) says when a manager applied or
//             discarded it, and the station then drops it.
//   rejected  the server refused it for good. It stays listed until someone at the station
//             acknowledges it ("OK" on the notice); the Admin keeps it under
//             Settings → Rejected station actions.
//   flagged   applied, with a note the people here should hear about (a later count stood,
//             stock moved during a count, a task that didn't match, a manager's PIN not
//             accepted). Listed until acknowledged, like a refusal. Never `needs_approval`:
//             a blind count's counter must not learn it was off (D-056).

import type { LocalReader, LocalTx } from './engine.js';

/** Held outcomes still waiting for a manager, and rejected ones nobody acknowledged yet. */
export const OUTCOME_COUNTS_SQL = `SELECT
  (SELECT count(*) FROM command_outcome o
    WHERE o.status = 'held'
      AND NOT EXISTS (SELECT 1 FROM command_hold h WHERE h.id = o.hold_id AND h.status != 'open')
  ) AS held,
  (SELECT count(*) FROM command_outcome
    WHERE status = 'rejected' AND acknowledged_at IS NULL) AS rejected`;

/** The open outcomes, oldest first (what the station notices name). */
export const OPEN_OUTCOMES_SQL = `SELECT o.id, o.type, o.status, o.reason, o.occurred_at
  FROM command_outcome o
  WHERE (o.status = 'held'
         AND NOT EXISTS (SELECT 1 FROM command_hold h WHERE h.id = o.hold_id AND h.status != 'open'))
     OR (o.status IN ('rejected', 'flagged') AND o.acknowledged_at IS NULL)
  ORDER BY o.occurred_at, o.id`;

/**
 * Flags on an applied command that the station tells its people about (M4 server fixes),
 * in the order they matter. `needs_approval` is never one of them (D-056).
 */
export const STATION_FLAGS = [
  'approval_not_valid',
  'before_later_count',
  'moved_during_count',
  'state_task_mismatch',
] as const;
export type StationFlag = (typeof STATION_FLAGS)[number];

/**
 * The station-facing flags of an answer, as stored in `command_outcome.reason`: codes
 * separated by commas, `approval_not_valid` with its reason (`approval_not_valid:approval_too_old`).
 * Null when none of them is there.
 */
export function stationFlagsText(
  flags: readonly { code: string; data?: Record<string, unknown> }[],
  command?: unknown,
): string | null {
  const parts: string[] = [];
  for (const code of STATION_FLAGS) {
    const flag = flags.find((f) => f.code === code);
    if (!flag) continue;
    if (code === 'moved_during_count' && onlyOwnOpenings(flag.data, command)) continue;
    const why = flag.data?.['reason'];
    parts.push(code === 'approval_not_valid' && typeof why === 'string' ? `${code}:${why}` : code);
  }
  return parts.length > 0 ? parts.join(',') : null;
}

/**
 * A count's "moved during the count" that lists only the openings of lots the count itself
 * carries (unlabelled stock given a lot while counting, D-054): nothing moved behind the
 * counter's back, so the station says nothing about it.
 */
function onlyOwnOpenings(data: Record<string, unknown> | undefined, command: unknown): boolean {
  const movements = data?.['movements'];
  if (!Array.isArray(movements) || movements.length === 0 || data?.['more'] === true) return false;
  const payload = (command as { payload?: { lines?: unknown } } | undefined)?.payload;
  const lines = Array.isArray(payload?.lines) ? (payload.lines as { lot_id?: unknown }[]) : [];
  const counted = new Set(lines.map((l) => l.lot_id).filter((id) => typeof id === 'string'));
  return movements.every(
    (m: { reason?: unknown; lot_id?: unknown }) =>
      m.reason === 'opening' && typeof m.lot_id === 'string' && counted.has(m.lot_id),
  );
}

/**
 * A refusal that only says the office got there first: a batch start or cancellation for a
 * batch the office had already cancelled or completed (UI5S-105). Nobody has to act on it:
 * the production queue shows it on the batch's card as information, so it raises no station
 * notice and doesn't count as "needs attention".
 */
export function calmRefusal(type: unknown, reason: unknown): boolean {
  return (
    (type === 'batch.start' || type === 'batch.cancel') &&
    typeof reason === 'string' &&
    ['already_cancelled', 'batch_cancelled', 'already_completed'].includes(reason)
  );
}

/** A stored flag (`code` or `code:reason`) in plain words for the people at the station. */
export function flagWords(stored: string): string {
  const [code, why] = stored.split(':');
  switch (code) {
    case 'approval_not_valid':
      return why === 'approval_too_old'
        ? 'the manager’s PIN was given too long before it was saved, so it didn’t count as approval'
        : why === 'approved_after_action'
          ? 'the manager’s PIN is timed after the action, so it didn’t count as approval'
          : 'the manager who approved it can’t approve that here, so it didn’t count as approval';
    case 'before_later_count':
      return 'it happened before a later count, so the count’s figures stand';
    case 'moved_during_count':
      return 'stock moved while it was being counted';
    case 'state_task_mismatch':
      return 'it didn’t match its task on today’s list, so the task stays open';
    default:
      return 'the office noted something about it';
  }
}

/** Held outcomes whose hold a manager has resolved (applied or discarded). */
export const RESOLVED_HOLDS_SQL = `SELECT o.id FROM command_outcome o
  JOIN command_hold h ON h.id = o.hold_id
  WHERE o.status = 'held' AND h.status != 'open'`;

export interface OutcomeCounts {
  held: number;
  rejected: number;
}

export async function readOutcomeCounts(db: LocalReader): Promise<OutcomeCounts> {
  const row = await db.getOptional<{ held: number | null; rejected: number | null }>(
    OUTCOME_COUNTS_SQL,
  );
  return { held: Number(row?.held ?? 0), rejected: Number(row?.rejected ?? 0) };
}

/** Drops held outcomes whose hold is resolved. Returns how many went. */
export async function pruneResolvedHolds(tx: LocalTx): Promise<number> {
  const rows = await tx.getAll<{ id: string }>(RESOLVED_HOLDS_SQL);
  for (const r of rows) await tx.execute('DELETE FROM command_outcome WHERE id = ?', [r.id]);
  return rows.length;
}

/** Marks every rejected outcome shown so far as seen ("OK" on the notice). */
export async function acknowledgeRejected(tx: LocalTx, now: Date): Promise<void> {
  await tx.execute(
    `UPDATE command_outcome SET acknowledged_at = ?
      WHERE status = 'rejected' AND acknowledged_at IS NULL`,
    [now.toISOString()],
  );
}

/** Marks every flagged outcome shown so far as seen ("OK" on its notice). */
export async function acknowledgeFlagged(tx: LocalTx, now: Date): Promise<void> {
  await tx.execute(
    `UPDATE command_outcome SET acknowledged_at = ?
      WHERE status = 'flagged' AND acknowledged_at IS NULL`,
    [now.toISOString()],
  );
}

/** A command type in plain words ("stock move"), for the notices. */
export function commandWords(type: string | null | undefined): string {
  switch (type) {
    case 'stock.state_move':
      return 'stock move';
    case 'label.print_logged':
      return 'label print record';
    case 'count.submit':
      return 'stock count';
    case 'writeoff.request':
      return 'write-off';
    case 'lot.create_opening':
      return 'new lot';
    case 'state_task.skip':
      return 'skipped state task';
    case 'batch.start':
      return 'batch start';
    case 'batch.complete':
      return 'batch completion';
    case 'batch.cancel':
      return 'batch cancellation';
    case 'receipt.record':
      return 'delivery';
    case 'order.handover':
      return 'order hand-over';
    default:
      return 'change';
  }
}
